import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Zap, Search, Bell, MoreHorizontal, ArrowUpRight, Clock, Globe, DollarSign, CreditCard, Activity, MapPin, CheckCircle2, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
const revenueData = [{
  name: 'Jan',
  retail: 4500,
  fee: 1200
}, {
  name: 'Feb',
  retail: 5200,
  fee: 1200
}, {
  name: 'Mar',
  retail: 4800,
  fee: 1200
}, {
  name: 'Apr',
  retail: 6100,
  fee: 1500
}, {
  name: 'May',
  retail: 7500,
  fee: 1800
}, {
  name: 'Jun',
  retail: 8900,
  fee: 2100
}, {
  name: 'Jul',
  retail: 9400,
  fee: 2200
}];
const sparklineData = [{
  value: 40
}, {
  value: 35
}, {
  value: 55
}, {
  value: 45
}, {
  value: 60
}, {
  value: 50
}, {
  value: 75
}];
const activities = [{
  id: 1,
  type: 'ticket',
  text: 'New Design Ticket from Nigeria',
  time: '2 mins ago',
  status: 'urgent'
}, {
  id: 2,
  type: 'payment',
  text: 'Payment Processed from Canada',
  time: '15 mins ago',
  status: 'success'
}, {
  id: 3,
  type: 'user',
  text: 'New Reseller Onboarded: Lagos Hub',
  time: '1 hour ago',
  status: 'info'
}, {
  id: 4,
  type: 'system',
  text: 'Server Maintenance Completed',
  time: '3 hours ago',
  status: 'success'
}, {
  id: 5,
  type: 'ticket',
  text: 'SLA Update: North Star Logistics',
  time: '5 hours ago',
  status: 'warning'
}];
const profitData = [{
  name: 'Mon',
  revenue: 4000,
  fee: 1200
}, {
  name: 'Tue',
  revenue: 3000,
  fee: 1200
}, {
  name: 'Wed',
  revenue: 5000,
  fee: 1500
}, {
  name: 'Thu',
  revenue: 4500,
  fee: 1500
}, {
  name: 'Fri',
  revenue: 6000,
  fee: 1800
}, {
  name: 'Sat',
  revenue: 5500,
  fee: 1800
}, {
  name: 'Sun',
  revenue: 7000,
  fee: 2100
}];
const bpoData = [{
  name: 'Upsell Active',
  value: 65
}, {
  name: 'Standard',
  value: 35
}];
const COLORS = ['#4F46E5', '#E2E8F0'];
export function Dashboard() {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-327">
      {/* SRE Status Bar */}
      <motion.div whileHover={{
      scale: 1.005
    }} className="bg-slate-900 px-6 py-2 rounded-lg flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest border border-slate-800 cursor-pointer group transition-all" data-id="element-328">
        <div className="flex items-center gap-6" data-id="element-329">
          <div className="flex items-center gap-2" data-id="element-330">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-331"></div>
            <span className="group-hover:text-white transition-colors" data-id="element-332">
              AMD EPYC Node: Online
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-333">
            <Activity size={12} className="text-indigo-400" data-id="element-334" />
            <span className="group-hover:text-white transition-colors" data-id="element-335">
              Ping: 14ms
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-336">
            <Globe size={12} className="text-indigo-400" data-id="element-337" />
            <span className="group-hover:text-white transition-colors" data-id="element-338">
              Global CDN: Optimal
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-339">
          <div className="flex items-center gap-2" data-id="element-340">
            <CheckCircle2 size={12} className="text-emerald-500" data-id="element-341" />
            <span className="group-hover:text-white transition-colors" data-id="element-342">
              Uptime: 99.99%
            </span>
          </div>
          <ChevronRight size={12} className="text-slate-600 group-hover:text-white" data-id="element-343" />
        </div>
      </motion.div>

      {/* Header with Greeting and Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-344">
        <div data-id="element-345">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-346">
            Partner Nerve Center
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-347">
            Welcome back, Alex. Here is your global network overview.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-1 max-w-md" data-id="element-348">
          <div className="relative w-full" data-id="element-349">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-350" />
            <input type="text" placeholder="Login as Customer..." className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all font-medium" data-id="element-351" />
          </div>
        </div>
      </div>

      {/* Top Row Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-352">
        {/* Profit Calculator Widget */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-353">
          <div className="flex items-center justify-between mb-6" data-id="element-354">
            <div data-id="element-355">
              <h3 className="font-bold text-slate-900" data-id="element-356">Profit Calculator</h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest" data-id="element-357">
                Gross Revenue vs. Base Fee
              </p>
            </div>
            <div className="text-right" data-id="element-358">
              <p className="text-lg font-bold text-emerald-600" data-id="element-359">+$4,200.00</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase" data-id="element-360">
                Net Profit (Weekly)
              </p>
            </div>
          </div>
          <div className="h-[200px]" data-id="element-361">
            <ResponsiveContainer width="100%" height="100%" data-id="element-362">
              <AreaChart data={profitData} data-id="element-363">
                <defs data-id="element-364">
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1" data-id="element-365">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} data-id="element-366" />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-367" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" data-id="element-368" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#94A3B8'
              }} data-id="element-369" />
                <YAxis hide data-id="element-370" />
                <Tooltip data-id="element-371" />
                <Area type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2} fill="url(#colorProfit)" data-id="element-372" />
                <Area type="monotone" dataKey="fee" stroke="#94A3B8" strokeWidth={2} fill="transparent" strokeDasharray="5 5" data-id="element-373" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Geographic Map Heatmap */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-374">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-375">Global Reach</h3>
          <div className="space-y-4" data-id="element-376">
            {[{
            region: 'North America',
            seats: 1250,
            color: 'bg-indigo-600'
          }, {
            region: 'Nigeria',
            seats: 860,
            color: 'bg-indigo-400'
          }, {
            region: 'Ghana',
            seats: 420,
            color: 'bg-indigo-300'
          }, {
            region: 'Liberia',
            seats: 170,
            color: 'bg-indigo-200'
          }].map(item => <div key={item.region} className="space-y-1" data-id="element-377">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest" data-id="element-378">
                  <span className="text-gray-500" data-id="element-379">{item.region}</span>
                  <span className="text-slate-900" data-id="element-380">{item.seats} Seats</span>
                </div>
                <div className="w-full bg-gray-50 h-1.5 rounded-full overflow-hidden" data-id="element-381">
                  <div className={`h-full ${item.color}`} style={{
                width: `${item.seats / 1500 * 100}%`
              }} data-id="element-382"></div>
                </div>
              </div>)}
          </div>
          <div className="mt-6 flex items-center gap-2 text-[10px] font-bold text-indigo-600 uppercase" data-id="element-383">
            <MapPin size={12} data-id="element-384" />
            <span data-id="element-385">4 Regions Active</span>
          </div>
        </div>

        {/* BPO Status Tracker */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-386">
          <h3 className="font-bold text-slate-900 mb-2" data-id="element-387">BPO Upsell</h3>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-4" data-id="element-388">
            $200/mo Marketing Tier
          </p>
          <div className="h-[140px] w-full" data-id="element-389">
            <ResponsiveContainer width="100%" height="100%" data-id="element-390">
              <PieChart data-id="element-391">
                <Pie data={bpoData} innerRadius={45} outerRadius={60} paddingAngle={5} dataKey="value" data-id="element-392">
                  {bpoData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-393" />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2" data-id="element-394">
            <p className="text-2xl font-bold text-slate-900" data-id="element-395">65%</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase" data-id="element-396">
              Utilization
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-397">
        {/* Main Chart Area */}
        <div className="lg:col-span-3 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-398">
          <div className="flex items-center justify-between mb-8" data-id="element-399">
            <div data-id="element-400">
              <h3 className="font-bold text-slate-900" data-id="element-401">
                Revenue vs. Fulfillment
              </h3>
              <p className="text-xs text-gray-500 font-medium" data-id="element-402">
                Tracking the split between retail price and NiishEngine base
                fees.
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-403">
              <div className="flex items-center gap-2" data-id="element-404">
                <div className="w-3 h-3 rounded-full bg-indigo-600" data-id="element-405"></div>
                <span className="text-xs font-bold text-gray-600" data-id="element-406">
                  Retail Price
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-407">
                <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-408"></div>
                <span className="text-xs font-bold text-gray-600" data-id="element-409">
                  Base Fee
                </span>
              </div>
              <select className="text-xs font-bold border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 outline-none" data-id="element-410">
                <option data-id="element-411">Last 7 Months</option>
                <option data-id="element-412">Year to Date</option>
              </select>
            </div>
          </div>

          <div className="h-[400px] w-full relative" data-id="element-413">
            {isLoading ? <div className="absolute inset-0 flex flex-col gap-4 animate-pulse" data-id="element-414">
                <div className="flex-1 bg-gray-50 rounded-xl" data-id="element-415"></div>
                <div className="h-8 flex gap-4" data-id="element-416">
                  <div className="w-16 bg-gray-50 rounded" data-id="element-417"></div>
                  <div className="w-16 bg-gray-50 rounded" data-id="element-418"></div>
                  <div className="w-16 bg-gray-50 rounded" data-id="element-419"></div>
                </div>
              </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-420">
                <AreaChart data={revenueData} data-id="element-421">
                  <defs data-id="element-422">
                    <linearGradient id="colorRetail" x1="0" y1="0" x2="0" y2="1" data-id="element-423">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} data-id="element-424" />
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-425" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" data-id="element-426" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94A3B8',
                fontWeight: 600
              }} dy={10} data-id="element-427" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94A3B8',
                fontWeight: 600
              }} domain={['auto', 'auto']} data-id="element-428" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
              }} data-id="element-429" />
                  <Area type="monotone" dataKey="retail" stroke="#4F46E5" strokeWidth={3} fillOpacity={1} fill="url(#colorRetail)" name="Retail Revenue" data-id="element-430" />
                  <Area type="monotone" dataKey="fee" stroke="#94A3B8" strokeWidth={2} fill="transparent" name="NiishEngine Fee" strokeDasharray="5 5" data-id="element-431" />
                </AreaChart>
              </ResponsiveContainer>}
          </div>
        </div>

        {/* Right Sidebar Widget: Activity Feed */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col" data-id="element-432">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-433">
            <h3 className="font-bold text-slate-900" data-id="element-434">Real-time Activity</h3>
            <button className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-435">
              <MoreHorizontal size={20} data-id="element-436" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-437">
            {activities.map(activity => <div key={activity.id} className="flex gap-4 group cursor-pointer" data-id="element-438">
                <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${activity.status === 'urgent' ? 'bg-red-500' : activity.status === 'success' ? 'bg-emerald-500' : activity.status === 'warning' ? 'bg-amber-500' : 'bg-indigo-500'}`} data-id="element-439"></div>
                <div className="space-y-1" data-id="element-440">
                  <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors leading-tight" data-id="element-441">
                    {activity.text}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-442">
                    <Clock size={10} data-id="element-443" />
                    {activity.time}
                  </div>
                </div>
              </div>)}
          </div>
          <div className="p-4 border-t border-gray-100" data-id="element-444">
            <button className="w-full py-2 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" data-id="element-445">
              View All Activity
            </button>
          </div>
        </div>
      </div>

      {/* Global Settings & Client Management Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-446">
        <div className="bg-slate-900 p-6 rounded-lg text-white relative overflow-hidden group cursor-pointer" data-id="element-447">
          <div className="relative z-10" data-id="element-448">
            <div className="flex items-center gap-3 mb-4" data-id="element-449">
              <div className="p-2 bg-white/10 rounded-lg" data-id="element-450">
                <Globe size={20} data-id="element-451" />
              </div>
              <h3 className="font-bold" data-id="element-452">Global Network Settings</h3>
            </div>
            <p className="text-sm text-slate-400 mb-6 max-w-xs" data-id="element-453">
              Manage multi-country operations, currencies, and regional tax
              compliance.
            </p>
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-400 group-hover:gap-3 transition-all" data-id="element-454">
              Configure Infrastructure <ArrowUpRight size={14} data-id="element-455" />
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform duration-500" data-id="element-456">
            <Globe size={160} data-id="element-457" />
          </div>
        </div>

        <div className="bg-indigo-600 p-6 rounded-lg text-white relative overflow-hidden group cursor-pointer" data-id="element-458">
          <div className="relative z-10" data-id="element-459">
            <div className="flex items-center gap-3 mb-4" data-id="element-460">
              <div className="p-2 bg-white/10 rounded-lg" data-id="element-461">
                <Users size={20} data-id="element-462" />
              </div>
              <h3 className="font-bold" data-id="element-463">Client Management</h3>
            </div>
            <p className="text-sm text-indigo-100 mb-6 max-w-xs" data-id="element-464">
              Jump into specific client dashboards, manage site SLAs, and handle
              support tickets.
            </p>
            <div className="flex items-center gap-2 text-xs font-bold text-white group-hover:gap-3 transition-all" data-id="element-465">
              Open CRM Portal <ArrowUpRight size={14} data-id="element-466" />
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform duration-500" data-id="element-467">
            <Users size={160} data-id="element-468" />
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 border-dashed flex flex-col items-center justify-center text-center space-y-3" data-id="element-469">
          <div className="p-3 bg-white rounded-full shadow-sm" data-id="element-470">
            <CreditCard size={24} className="text-gray-300" data-id="element-471" />
          </div>
          <div data-id="element-472">
            <h3 className="text-sm font-bold text-gray-400" data-id="element-473">
              Virtual Terminal
            </h3>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest" data-id="element-474">
              Coming Soon
            </p>
          </div>
        </div>
      </div>
    </div>;
}